// distanceUtils.js - Firebase Integration with managedBikes collection
// Utility functions for distance calculation and pricing with Firebase backend

import { db } from '../firebase';
import { doc, getDoc, collection, query, getDocs } from 'firebase/firestore';

// Cache for configuration to avoid repeated Firebase calls
let configCache = null;
let cacheTimestamp = null;
let bikeModelsCache = null;
let bikeModelsCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ✅ NEW: Function to get bike models from Firebase managedBikes collection
export const getBikeModelsFromFirebase = async (companyId = null) => {
  try {
    // Check if we have valid cached bike models
    if (bikeModelsCache && bikeModelsCacheTimestamp && 
        (Date.now() - bikeModelsCacheTimestamp < CACHE_DURATION)) {
      return bikeModelsCache;
    }

    console.log(`🚗 Loading bike models from Firebase managedBikes collection${companyId ? ` for company: ${companyId}` : ' (all companies)'}`);
    
    const managedBikesCollection = collection(db, 'managedBikes');
    let querySnapshot;
    
    if (companyId) {
      // Get bikes for specific company
      const q = query(managedBikesCollection, where('companyId', '==', companyId));
      querySnapshot = await getDocs(q);
    } else {
      // Get all bikes (fallback for legacy compatibility)
      querySnapshot = await getDocs(managedBikesCollection);
    }
    
    const uniqueModels = new Set();
    const bikeModelRates = {};
    
    querySnapshot.forEach((doc) => {
      const bikeData = doc.data();
      if (bikeData.model) {
        const modelKey = bikeData.model.replace(/[0-9]/g, '').trim().toUpperCase();
        uniqueModels.add(bikeData.model);
        
        // Track which company the bike belongs to for debugging
        if (bikeData.companyId) {
          console.log(`📊 Found bike model: ${bikeData.model} (${bikeData.companyId})`);
        }
      }
    });
    
    const bikeModels = Array.from(uniqueModels).sort();
    
    // Cache the results
    bikeModelsCache = bikeModels;
    bikeModelsCacheTimestamp = Date.now();
    
    console.log(`✅ Loaded ${bikeModels.length} unique bike models from Firebase:`, bikeModels);
    return bikeModels;
    
  } catch (error) {
    console.error('Error loading bike models from Firebase:', error);
    
    // Fallback to cached data if available
    if (bikeModelsCache) {
      console.warn('Using cached bike models due to Firebase error');
      return bikeModelsCache;
    }
    
    // Final fallback to empty array
    console.warn('No bike models available, returning empty array');
    return [];
  }
};

// ✅ NEW: Function to get bike data for specific model from Firebase
export const getBikeDataFromFirebase = async (bikeModel, companyId = null) => {
  try {
    console.log(`🔍 Looking up bike data for model: ${bikeModel}${companyId ? ` in company: ${companyId}` : ''}`);
    
    const managedBikesCollection = collection(db, 'managedBikes');
    let querySnapshot;
    
    if (companyId) {
      const q = query(
        managedBikesCollection, 
        where('companyId', '==', companyId),
        where('model', '==', bikeModel)
      );
      querySnapshot = await getDocs(q);
    } else {
      const q = query(managedBikesCollection, where('model', '==', bikeModel));
      querySnapshot = await getDocs(q);
    }
    
    if (!querySnapshot.empty) {
      const bikeDoc = querySnapshot.docs[0];
      const bikeData = bikeDoc.data();
      console.log(`✅ Found bike data for ${bikeModel}:`, {
        model: bikeData.model,
        dailyRate: bikeData.dailyRate,
        companyId: bikeData.companyId
      });
      return bikeData;
    } else {
      console.warn(`⚠️ No bike data found for model: ${bikeModel}`);
      return null;
    }
    
  } catch (error) {
    console.error(`Error looking up bike data for ${bikeModel}:`, error);
    return null;
  }
};

// ✅ NEW: Function to invalidate bike models cache
export const invalidateBikeModelsCache = () => {
  bikeModelsCache = null;
  bikeModelsCacheTimestamp = null;
  console.log('Bike models cache invalidated');
};

// Function to get distance pricing configuration from Firebase
export const getDistancePricingConfig = async () => {
  try {
    // Check if we have a valid cached configuration
    if (configCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return configCache.distancePricing;
    }

    // Fetch configuration from Firebase
    const configDocRef = doc(db, 'configurations', 'app_config');
    const configDoc = await getDoc(configDocRef);
    
    if (configDoc.exists()) {
      const firebaseConfig = configDoc.data();
      
      // Update cache
      configCache = firebaseConfig;
      cacheTimestamp = Date.now();
      
      // Check if deliveryPricing is configured - new format
      if (firebaseConfig.deliveryPricing && firebaseConfig.deliveryPricing.length > 0) {
        // Convert the array-based deliveryPricing to the format expected by the app
        const bikeModelRates = {};
        const bikeModelMinimums = {};
        
        // Map each entry in the deliveryPricing array to the bikeModelRates object and minimums
        firebaseConfig.deliveryPricing.forEach(pricing => {
          const upperModel = pricing.model.toUpperCase();
          bikeModelRates[upperModel] = pricing.pricePerKm;
          bikeModelMinimums[upperModel] = pricing.minimumCharge || 5; // Default to 5 if not set
        });
        
        return {
          enabled: true,
          bikeModelRates: bikeModelRates,
          bikeModelMinimums: bikeModelMinimums,
          minimumCharge: 5, // Default minimum charge as fallback
          chargeOnlyOneWay: false // Default to charge both ways
        };
      }
      
      // Fall back to the old format or defaults if no deliveryPricing
      return firebaseConfig.distancePricing || {
        enabled: false,
        bikeModelRates: { DEFAULT: 0.6 },
        bikeModelMinimums: { DEFAULT: 5 },
        minimumCharge: 5,
        chargeOnlyOneWay: false
      };
    } else {
      console.warn('No configuration document found in Firebase');
      return getDefaultDistancePricingConfig();
    }
  } catch (error) {
    console.error('Error loading distance pricing configuration from Firebase:', error);
    
    // Fallback to localStorage if Firebase fails
    try {
      return getDistancePricingConfigFromLocalStorage();
    } catch (localError) {
      console.error('Fallback to localStorage also failed:', localError);
      return getDefaultDistancePricingConfig();
    }
  }
};

// Synchronous version that tries to use cached data or falls back to localStorage
export const getDistancePricingConfigSync = () => {
  try {
    // Check if we have valid cached data
    if (configCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      if (configCache.deliveryPricing && configCache.deliveryPricing.length > 0) {
        const bikeModelRates = {};
        const bikeModelMinimums = {};
        
        configCache.deliveryPricing.forEach(pricing => {
          const upperModel = pricing.model.toUpperCase();
          bikeModelRates[upperModel] = pricing.pricePerKm;
          bikeModelMinimums[upperModel] = pricing.minimumCharge || 5;
        });
        
        return {
          enabled: true,
          bikeModelRates: bikeModelRates,
          bikeModelMinimums: bikeModelMinimums,
          minimumCharge: 5,
          chargeOnlyOneWay: false
        };
      }
    }

    // Fallback to localStorage
    return getDistancePricingConfigFromLocalStorage();
  } catch (error) {
    console.error('Error in synchronous config retrieval:', error);
    return getDefaultDistancePricingConfig();
  }
};

// Fallback function to read from localStorage (for backward compatibility)
const getDistancePricingConfigFromLocalStorage = () => {
  try {
    const apiConfig = localStorage.getItem('apiConfig');
    if (apiConfig) {
      const config = JSON.parse(apiConfig);
      
      // Check if deliveryPricing is configured - new format
      if (config.deliveryPricing && config.deliveryPricing.length > 0) {
        const bikeModelRates = {};
        const bikeModelMinimums = {};
        
        config.deliveryPricing.forEach(pricing => {
          const upperModel = pricing.model.toUpperCase();
          bikeModelRates[upperModel] = pricing.pricePerKm;
          bikeModelMinimums[upperModel] = pricing.minimumCharge || 5;
        });
        
        return {
          enabled: true,
          bikeModelRates: bikeModelRates,
          bikeModelMinimums: bikeModelMinimums,
          minimumCharge: 5,
          chargeOnlyOneWay: false
        };
      }
      
      return config.distancePricing || getDefaultDistancePricingConfig();
    }
  } catch (error) {
    console.error('Error loading distance pricing configuration from localStorage:', error);
  }
  
  return getDefaultDistancePricingConfig();
};

// Default configuration
const getDefaultDistancePricingConfig = () => {
  return {
    enabled: false,
    bikeModelRates: { DEFAULT: 0.6 },
    bikeModelMinimums: { DEFAULT: 5 },
    minimumCharge: 5,
    chargeOnlyOneWay: false
  };
};

// Function to get rental office coordinates from Firebase
export const getRentalOfficeCoordinates = async () => {
  try {
    // Check cached configuration first
    if (configCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      if (configCache.rentalOfficeLocation && 
          configCache.rentalOfficeLocation.latitude && 
          configCache.rentalOfficeLocation.longitude) {
        return { 
          lat: configCache.rentalOfficeLocation.latitude, 
          lng: configCache.rentalOfficeLocation.longitude 
        };
      }
    }

    // Fetch from Firebase if not in cache
    const configDocRef = doc(db, 'configurations', 'app_config');
    const configDoc = await getDoc(configDocRef);
    
    if (configDoc.exists()) {
      const firebaseConfig = configDoc.data();
      
      // Update cache
      configCache = firebaseConfig;
      cacheTimestamp = Date.now();
      
      // Check for the new rentalOfficeLocation format
      if (firebaseConfig.rentalOfficeLocation && 
          firebaseConfig.rentalOfficeLocation.latitude && 
          firebaseConfig.rentalOfficeLocation.longitude) {
        return { 
          lat: firebaseConfig.rentalOfficeLocation.latitude, 
          lng: firebaseConfig.rentalOfficeLocation.longitude 
        };
      }
      
      // Fall back to the old rentalOfficeCoordinates format
      if (firebaseConfig.rentalOfficeCoordinates) {
        return firebaseConfig.rentalOfficeCoordinates;
      }
    }
    
    // Fallback to localStorage if Firebase fails
    return getRentalOfficeCoordinatesFromLocalStorage();
  } catch (error) {
    console.error('Error loading rental office coordinates from Firebase:', error);
    
    // Fallback to localStorage
    return getRentalOfficeCoordinatesFromLocalStorage();
  }
};

// Synchronous version for immediate use
export const getRentalOfficeCoordinatesSync = () => {
  try {
    // Check cached configuration first
    if (configCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      if (configCache.rentalOfficeLocation && 
          configCache.rentalOfficeLocation.latitude && 
          configCache.rentalOfficeLocation.longitude) {
        return { 
          lat: configCache.rentalOfficeLocation.latitude, 
          lng: configCache.rentalOfficeLocation.longitude 
        };
      }
    }

    // Fallback to localStorage
    return getRentalOfficeCoordinatesFromLocalStorage();
  } catch (error) {
    console.error('Error in synchronous coordinates retrieval:', error);
    return getDefaultRentalOfficeCoordinates();
  }
};

// Fallback function to read coordinates from localStorage
const getRentalOfficeCoordinatesFromLocalStorage = () => {
  try {
    const apiConfig = localStorage.getItem('apiConfig');
    if (apiConfig) {
      const config = JSON.parse(apiConfig);
      
      // First check for the new rentalOfficeLocation format
      if (config.rentalOfficeLocation && 
          config.rentalOfficeLocation.latitude && 
          config.rentalOfficeLocation.longitude) {
        return { 
          lat: config.rentalOfficeLocation.latitude, 
          lng: config.rentalOfficeLocation.longitude 
        };
      }
      
      // Fall back to the old rentalOfficeCoordinates format
      if (config.rentalOfficeCoordinates) {
        return config.rentalOfficeCoordinates;
      }
    }
  } catch (error) {
    console.error('Error loading rental office coordinates from localStorage:', error);
  }
  
  return getDefaultRentalOfficeCoordinates();
};

// Default coordinates
const getDefaultRentalOfficeCoordinates = () => {
  // Default coordinates (Kandy, Sri Lanka) - only used if no configuration is found
  return { lat: 7.2906, lng: 80.6337 };
};

// Function to invalidate cache (useful when configuration is updated)
export const invalidateConfigCache = () => {
  configCache = null;
  cacheTimestamp = null;
  console.log('Configuration cache invalidated');
};

// Function to preload configuration (useful for app initialization)
export const preloadConfiguration = async () => {
  try {
    await getDistancePricingConfig();
    await getRentalOfficeCoordinates();
    console.log('Configuration preloaded successfully');
    return true;
  } catch (error) {
    console.error('Error preloading configuration:', error);
    return false;
  }
};

// Function to calculate distance between two points using the Haversine formula
// This is used as a fallback when Google Distance Matrix API fails
export const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  // Convert latitude and longitude from degrees to radians
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * 
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  // Radius of the Earth in kilometers
  const radius = 6371;
  
  // Calculate the distance
  return radius * c;
};

// Function to calculate road distance using the Google Maps Directions API
// This calculates actual driving distance through roads
export const calculateGoogleMapsDistance = async (origin, destination) => {
  return new Promise((resolve, reject) => {
    try {
      // Check if Google Maps API is loaded
      if (!window.google || !window.google.maps) {
        console.warn('Google Maps API not loaded, trying fallback...');
        throw new Error('Google Maps API not loaded');
      }
      
      // Create a new DirectionsService instance
      const directionsService = new window.google.maps.DirectionsService();
      
      // Set up the request for directions
      const request = {
        origin: origin,
        destination: destination,
        travelMode: window.google.maps.TravelMode.DRIVING, // Calculate driving distance
        unitSystem: window.google.maps.UnitSystem.METRIC // Return distance in kilometers
      };
      
      // Make the request to the Directions API
      directionsService.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          // Extract the total distance from the result
          const route = result.routes[0];
          
          // Sum up all leg distances (in case there are multiple legs)
          let totalDistance = 0;
          route.legs.forEach(leg => {
            totalDistance += leg.distance.value; // This is in meters
          });
          
          // Convert from meters to kilometers
          const distanceInKm = totalDistance / 1000;
          console.log('Google Directions API distance:', distanceInKm.toFixed(2), 'km');
          resolve(distanceInKm);
        } else {
          console.warn('Directions service failed with status:', status, 'trying Distance Matrix fallback...');
          // If DirectionsService fails, try using DistanceMatrixService as fallback
          fallbackToDistanceMatrix(origin, destination, resolve, reject);
        }
      });
    } catch (error) {
      console.error('Error in Directions service:', error);
      // Try the fallback if DirectionsService fails
      fallbackToDistanceMatrix(origin, destination, resolve, reject);
    }
  });
};

// Fallback to DistanceMatrixService if DirectionsService fails
const fallbackToDistanceMatrix = (origin, destination, resolve, reject) => {
  try {
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded for Distance Matrix, trying Haversine fallback...');
      fallbackToHaversine(origin, destination, resolve, reject);
      return;
    }
    
    // Create a new Distance Matrix service instance
    const distanceMatrixService = new window.google.maps.DistanceMatrixService();
    
    // Make the API request
    distanceMatrixService.getDistanceMatrix(
      {
        origins: [origin],
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC
      },
      (response, status) => {
        if (status === 'OK') {
          // Extract the distance value from the API response
          if (
            response.rows &&
            response.rows[0] &&
            response.rows[0].elements &&
            response.rows[0].elements[0] &&
            response.rows[0].elements[0].status === 'OK'
          ) {
            // Distance in meters
            const distanceInMeters = response.rows[0].elements[0].distance.value;
            // Convert to kilometers
            const distanceInKm = distanceInMeters / 1000;
            console.log('Google Distance Matrix API distance:', distanceInKm.toFixed(2), 'km');
            resolve(distanceInKm);
          } else {
            console.warn('Distance Matrix returned invalid response, trying Haversine fallback...');
            // If all else fails, fall back to Haversine distance
            fallbackToHaversine(origin, destination, resolve, reject);
          }
        } else {
          console.warn('Distance Matrix service failed with status:', status, 'trying Haversine fallback...');
          // If Distance Matrix fails, fall back to Haversine distance
          fallbackToHaversine(origin, destination, resolve, reject);
        }
      }
    );
  } catch (error) {
    console.error('Error in Distance Matrix service:', error);
    // Fall back to Haversine if all Google services fail
    fallbackToHaversine(origin, destination, resolve, reject);
  }
};

// Final fallback to Haversine if all Google services fail
const fallbackToHaversine = (origin, destination, resolve, reject) => {
  try {
    // Check if we have valid coordinates
    if (origin.lat && origin.lng && destination.lat && destination.lng) {
      const haversineDistance = calculateHaversineDistance(
        origin.lat, 
        origin.lng, 
        destination.lat, 
        destination.lng
      );
      
      // Apply a rough correction factor to make Haversine more similar to road distance
      // Road distance is typically 20-30% longer than straight-line distance
      const estimatedRoadDistance = haversineDistance * 1.3;
      
      console.log('Fallback Haversine distance (with road correction):', estimatedRoadDistance.toFixed(2), 'km');
      console.warn('Using estimated road distance as fallback. For accurate road distances, ensure Google Maps API is properly loaded.');
      
      resolve(estimatedRoadDistance);
    } else {
      console.error('Invalid coordinates for distance calculation:', origin, destination);
      reject(new Error('Invalid coordinates for distance calculation'));
    }
  } catch (error) {
    console.error('Haversine calculation failed:', error);
    reject(error);
  }
};

// ✅ UPDATED: Function to calculate single delivery fee using Firebase bike models
export const calculateSingleDeliveryFee = async (distance, bikeModel, companyId = null) => {
  try {
    const distancePricing = await getDistancePricingConfig();
    
    // If distance pricing is not enabled, return 0
    if (!distancePricing.enabled) {
      console.log('Distance pricing is not enabled');
      return 0;
    }
    
    // Skip calculation if bike model isn't provided
    if (!bikeModel) {
      console.log('No bike model provided');
      return 0;
    }
    
    // ✅ NEW: Validate bike model exists in Firebase
    const bikeData = await getBikeDataFromFirebase(bikeModel, companyId);
    if (bikeData) {
      console.log(`✅ Validated bike model ${bikeModel} exists in Firebase (Company: ${bikeData.companyId})`);
    } else {
      console.warn(`⚠️ Bike model ${bikeModel} not found in Firebase, using default rates`);
    }
    
    // Clean up the bike model string to ensure proper lookup
    const modelKey = bikeModel.replace(/[0-9]/g, '').trim().toUpperCase();
    console.log('Looking up rate for model:', modelKey);
    
    // Get the rate for this bike model
    const rate = distancePricing.bikeModelRates[modelKey] || 
                  distancePricing.bikeModelRates.DEFAULT || 0.6;
    
    // Get the minimum charge for this bike model
    const modelMinimumCharge = distancePricing.bikeModelMinimums[modelKey] || 
                              distancePricing.bikeModelMinimums.DEFAULT || 
                              distancePricing.minimumCharge || 5;
    
    console.log('Firebase bike validation:', bikeData ? 'FOUND' : 'NOT FOUND');
    console.log('Rate found:', rate);
    console.log('Minimum charge for model:', modelMinimumCharge);
    console.log('Distance:', distance);
    
    // Calculate the fee
    let fee = distance * rate;
    
    // Apply minimum charge if applicable
    if (fee > 0 && fee < modelMinimumCharge) {
      fee = modelMinimumCharge;
      console.log('Applying minimum charge');
    }
    
    // Round to 2 decimal places
    fee = Math.round(fee * 100) / 100;
    
    console.log('Calculated fee:', fee);
    return fee;
  } catch (error) {
    console.error('Error calculating single delivery fee:', error);
    return 0;
  }
};

// Synchronous version for immediate calculations
export const calculateSingleDeliveryFeeSync = (distance, bikeModel) => {
  try {
    const distancePricing = getDistancePricingConfigSync();
    
    // If distance pricing is not enabled, return 0
    if (!distancePricing.enabled) {
      console.log('Distance pricing is not enabled');
      return 0;
    }
    
    // Skip calculation if bike model isn't provided
    if (!bikeModel) {
      console.log('No bike model provided');
      return 0;
    }
    
    // Clean up the bike model string to ensure proper lookup
    const modelKey = bikeModel.replace(/[0-9]/g, '').trim().toUpperCase();
    
    // Get the rate for this bike model
    const rate = distancePricing.bikeModelRates[modelKey] || 
                  distancePricing.bikeModelRates.DEFAULT || 0.6;
    
    // Get the minimum charge for this bike model
    const modelMinimumCharge = distancePricing.bikeModelMinimums[modelKey] || 
                              distancePricing.bikeModelMinimums.DEFAULT || 
                              distancePricing.minimumCharge || 5;
    
    // Calculate the fee
    let fee = distance * rate;
    
    // Apply minimum charge if applicable
    if (fee > 0 && fee < modelMinimumCharge) {
      fee = modelMinimumCharge;
    }
    
    // Round to 2 decimal places
    fee = Math.round(fee * 100) / 100;
    
    return fee;
  } catch (error) {
    console.error('Error in synchronous fee calculation:', error);
    return 0;
  }
};

// ✅ UPDATED: Function to calculate fee based on distance and Firebase bike model
export const calculateDistanceFee = async (
  pickupLocation,
  pickupCoordinates,
  dropoffLocation, 
  dropoffCoordinates,
  bikeModel,
  setPickupDistance,
  setDropoffDistance,
  setIsCalculatingDistance,
  companyId = null
) => {
  try {
    const [distancePricing, rentalOfficeCoordinates] = await Promise.all([
      getDistancePricingConfig(),
      getRentalOfficeCoordinates()
    ]);
    
    // If distance pricing is not enabled, return 0
    if (!distancePricing.enabled) {
      if (setPickupDistance) setPickupDistance(0);
      if (setDropoffDistance) setDropoffDistance(0);
      return 0;
    }
    
    // Skip calculation if bike model isn't provided
    if (!bikeModel) {
      if (setPickupDistance) setPickupDistance(0);
      if (setDropoffDistance) setDropoffDistance(0);
      return 0;
    }
    
    // ✅ NEW: Validate bike model exists in Firebase
    const bikeData = await getBikeDataFromFirebase(bikeModel, companyId);
    if (bikeData) {
      console.log(`✅ Using Firebase bike data for fee calculation: ${bikeModel} (${bikeData.companyId})`);
    }
    
    if (setIsCalculatingDistance) setIsCalculatingDistance(true);
    
    let totalDistance = 0;
    let pickupDistanceValue = 0;
    let dropoffDistanceValue = 0;
    
    // Calculate pickup distance if custom location is selected
    if (pickupLocation === 'custom' && pickupCoordinates) {
      pickupDistanceValue = await calculateGoogleMapsDistance(
        rentalOfficeCoordinates,
        pickupCoordinates
      );
      if (setPickupDistance) setPickupDistance(pickupDistanceValue);
      totalDistance += pickupDistanceValue;
    }
    
    // Calculate dropoff distance if custom location is selected
    if (dropoffLocation === 'custom' && dropoffCoordinates) {
      dropoffDistanceValue = await calculateGoogleMapsDistance(
        rentalOfficeCoordinates,
        dropoffCoordinates
      );
      if (setDropoffDistance) setDropoffDistance(dropoffDistanceValue);
      totalDistance += dropoffDistanceValue;
    }
    
    // If we're only charging for one way (pickup OR dropoff, not both)
    if (distancePricing.chargeOnlyOneWay) {
      totalDistance = Math.max(pickupDistanceValue, dropoffDistanceValue);
    }
    
    // Clean up the bike model string to ensure proper lookup
    const modelKey = bikeModel.replace(/[0-9]/g, '').trim().toUpperCase();
    
    // Get the rate for this bike model
    const ratePerKm = distancePricing.bikeModelRates[modelKey] || 
                      distancePricing.bikeModelRates.DEFAULT || 0.6;
    
    // Get the minimum charge for this bike model
    const modelMinimumCharge = distancePricing.bikeModelMinimums[modelKey] || 
                              distancePricing.bikeModelMinimums.DEFAULT || 
                              distancePricing.minimumCharge || 5;
    
    // Calculate the fee
    let fee = totalDistance * ratePerKm;
    
    // Apply minimum charge if applicable
    if (fee > 0 && fee < modelMinimumCharge) {
      fee = modelMinimumCharge;
    }
    
    // Round to 2 decimal places
    fee = Math.round(fee * 100) / 100;
    
    console.log(`💰 Distance fee calculated using Firebase bike data: $${fee} (Model: ${bikeModel}, Company: ${bikeData?.companyId || 'Unknown'})`);
    
    return fee;
  } catch (error) {
    console.error('Error calculating distance fee:', error);
    return 0;
  } finally {
    if (setIsCalculatingDistance) setIsCalculatingDistance(false);
  }
};

// Helper function to calculate rental duration
export const calculateRentalDuration = (pickupDate, dropoffDate) => {
  if (!pickupDate || !dropoffDate) return 0;
  const start = new Date(pickupDate);
  const end = new Date(dropoffDate);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
};